import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../hooks/useApi";
import GrcControlWorkflow from "./GrcControlWorkflow";
import { isClosedRisk } from "../hooks/useGrcRiskWorkflow";
import FrameworkReadinessChart from "./FrameworkReadinessChart";

// Security Insights → Frameworks: one card per framework the organisation is scored on (the
// summary's compliance estimates), each opening its requirement list from
// GET /security-insights/framework-requirements. Status and mapped controls come from the server,
// which computes them from the same rows as the coverage card, so the two never disagree.
// Opening a requirement shows its controls with their evidence and the point 1 risk workflow, and
// each control opens in the Controls tab; a control's "View requirements" comes back here (focus).

export const STATUS_LABEL = {
    Pass: "Pass",
    Partial: "Partial",
    Fail: "Fail",
    NotAssessed: "Not assessed",
    NotApplicable: "Not applicable",
    FutureEffective: "Future effective",
};

const STATUS_STYLE = {
    Pass: "bg-green-50 text-green-700 border-green-200",
    Partial: "bg-amber-50 text-amber-700 border-amber-200",
    Fail: "bg-red-50 text-red-700 border-red-200",
    NotAssessed: "bg-gray-50 text-gray-600 border-gray-200",
    NotApplicable: "bg-gray-50 text-gray-400 border-gray-200",
    FutureEffective: "bg-blue-50 text-blue-700 border-blue-200",
};

const STATUS_ORDER = ["Fail", "Partial", "Pass", "NotAssessed", "FutureEffective", "NotApplicable"];

// The card's headline: readiness for scorable frameworks, counts for an unscorable one, a dash when
// nothing is evidenced (never 0%).
export const readinessText = (estimate) => {
    if (!estimate || !estimate.observedControls) return "—";
    if (estimate.readinessBasis === "Unscorable")
        return `${estimate.passingControls} pass · ${estimate.partialControls} partial · ${estimate.failingControls} fail`;
    return `${Math.round(estimate.adjustedCoveragePercent)}%`;
};

export const filterRequirements = (requirements, { status = "all", search = "" } = {}) => {
    const q = search.trim().toLowerCase();
    return (requirements || []).filter(r =>
        (status === "all" || r.status === status)
        && (!q || `${r.displayId} ${r.title} ${r.group}`.toLowerCase().includes(q)));
};

// Groups in the order the framework lists them (the server returns catalog order).
export const groupRequirements = (requirements) => {
    const groups = [];
    const byName = new Map();
    for (const r of requirements || []) {
        const name = r.group || "Other";
        if (!byName.has(name)) {
            const group = { name, items: [] };
            byName.set(name, group);
            groups.push(group);
        }
        byName.get(name).items.push(r);
    }
    return groups;
};

export const statusCounts = (requirements) => {
    const counts = {};
    for (const r of requirements || []) counts[r.status] = (counts[r.status] || 0) + 1;
    return counts;
};

// The open risks of a requirement: every non-closed risk of its controls, each risk once.
export const openIssuesOf = (requirement, risksForControl) => {
    const seen = new Set();
    const risks = [];
    for (const c of requirement.controls || []) {
        for (const r of risksForControl(c) || []) {
            if (isClosedRisk(r) || seen.has(String(r.id))) continue;
            seen.add(String(r.id));
            risks.push(r);
        }
    }
    return risks;
};

// The owner filter: mine = owned by this user; unassigned = nobody owns it.
export const matchesOwnerFilter = (requirement, filter, userId) => {
    if (filter === "mine") return userId != null && requirement.ownerUserId === userId;
    if (filter === "unassigned") return requirement.ownerUserId == null;
    return true;
};

export const ownerLabel = (requirement) => {
    if (requirement.ownerUserId == null) return "Unassigned";
    if (requirement.ownerLeft) return "Owner left";
    return requirement.ownerName || "Unknown";
};

const CONTROL_STATUS_LABEL = { Implemented: "Implemented", Partial: "Partial", NotImplemented: "Not implemented", NotAssessed: "Not assessed" };

// Who owns the requirement; a manager can change it, clear it, or confirm it after the text changed.
const RequirementOwner = ({ requirement, members, canManageOwners, onSetOwner }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const save = async (ownerUserId) => {
        setSaving(true);
        setError(null);
        try {
            await onSetOwner(requirement, ownerUserId);
        } catch (err) {
            setError(err?.response?.data?.message || "Could not save the owner. Try again.");
        } finally {
            setSaving(false);
        }
    };
    const hasOwner = requirement.ownerUserId != null;
    return (
        <div className="flex flex-wrap items-center gap-2 text-xs mb-2" data-testid={`grc-requirement-owner-${requirement.id}`}>
            <span className="text-gray-600">Owner:</span>
            <span className={`font-medium ${hasOwner && !requirement.ownerLeft ? "text-gray-800" : "text-amber-700"}`}>{ownerLabel(requirement)}</span>
            {requirement.ownerLeft && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">No longer a member — reassign</span>}
            {hasOwner && !requirement.ownerLeft && requirement.requirementChanged && (
                <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">Requirement changed — confirm</span>
            )}
            {canManageOwners && (
                <>
                    <select aria-label="Requirement owner" disabled={saving}
                        value={hasOwner && !requirement.ownerLeft ? String(requirement.ownerUserId) : ""}
                        onChange={e => save(e.target.value === "" ? null : Number(e.target.value))}
                        className="border border-gray-200 rounded px-1.5 py-0.5 bg-white">
                        <option value="">{hasOwner ? "Remove owner" : "Choose an owner"}</option>
                        {(members || []).map(m => <option key={m.id} value={m.id}>{m.fullName}</option>)}
                    </select>
                    {hasOwner && !requirement.ownerLeft && requirement.requirementChanged && (
                        <button type="button" disabled={saving} onClick={() => save(requirement.ownerUserId)}
                            className="px-2 py-0.5 border border-blue-200 rounded text-blue-700 hover:bg-blue-50">
                            Confirm owner
                        </button>
                    )}
                </>
            )}
            {error && <span className="text-red-600" role="alert">{error}</span>}
        </div>
    );
};

const EVIDENCE_STATUS_STYLE = {
    Satisfied: "bg-green-50 text-green-700 border-green-200",
    Pending: "bg-gray-50 text-gray-600 border-gray-200",
    Overdue: "bg-red-50 text-red-700 border-red-200",
    Expired: "bg-amber-50 text-amber-700 border-amber-200",
};

const formatDue = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : null;
};

// The evidence requests linked to a requirement, each with its own status; a manager links and unlinks.
// A link is navigation only: it never changes the requirement's status.
const RequirementEvidence = ({ requirement, evidenceRequests, canManageOwners, onSetEvidenceLink }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const linked = requirement.evidenceRequests || [];
    const linkedIds = new Set(linked.map(r => r.id));
    const candidates = (evidenceRequests || []).filter(r => !linkedIds.has(r.id));
    const change = async (evidenceRequestId, isLinked) => {
        setSaving(true);
        setError(null);
        try {
            await onSetEvidenceLink(requirement, evidenceRequestId, isLinked);
        } catch (err) {
            setError(err?.response?.data?.message || "Could not update the link. Try again.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="mb-2" data-testid={`grc-requirement-evidence-${requirement.id}`}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence requests</div>
            {linked.length === 0
                ? <div className="text-xs text-gray-400">No evidence request is linked to this requirement.</div>
                : (
                    <ul className="space-y-1">
                        {linked.map(r => (
                            <li key={r.id} className="text-xs flex flex-wrap items-center gap-2">
                                <span className="text-gray-800">{r.title}</span>
                                <span className={`px-1.5 py-0.5 rounded border font-semibold ${EVIDENCE_STATUS_STYLE[r.status] || EVIDENCE_STATUS_STYLE.Pending}`}>{r.status}</span>
                                {formatDue(r.dueDate) && <span className="text-gray-500">Due {formatDue(r.dueDate)}</span>}
                                {canManageOwners && (
                                    <button type="button" disabled={saving} onClick={() => change(r.id, false)}
                                        className="text-gray-500 hover:text-red-600 hover:underline">Unlink</button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            {canManageOwners && candidates.length > 0 && (
                <select aria-label="Link an evidence request" disabled={saving} value=""
                    onChange={e => e.target.value && change(Number(e.target.value), true)}
                    className="mt-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white">
                    <option value="">Link an evidence request…</option>
                    {candidates.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                </select>
            )}
            <div className="text-xs text-gray-400 mt-1">An evidence request's status is its own; linking it does not change this requirement's status.</div>
            {error && <div className="text-xs text-red-600" role="alert">{error}</div>}
        </div>
    );
};

// One requirement opened: its owner, its evidence requests, its controls, each with the evidence it
// reports and its risk workflow.
const RequirementDetail = ({
    requirement, controlsById, workflow, onWorkflowChanged, workflowBusyReason, onOpenControl,
    members, canManageOwners, onSetOwner, evidenceRequestsEnabled, evidenceRequests, onSetEvidenceLink,
}) => {
    const risksForControl = workflow?.risksForControl || (() => []);
    const issues = openIssuesOf(requirement, risksForControl);
    return (
        <div className="px-3 pb-3" data-testid={`grc-requirement-detail-${requirement.id}`}>
            <RequirementOwner requirement={requirement} members={members} canManageOwners={canManageOwners} onSetOwner={onSetOwner} />
            {evidenceRequestsEnabled && (
                <RequirementEvidence requirement={requirement} evidenceRequests={evidenceRequests}
                    canManageOwners={canManageOwners} onSetEvidenceLink={onSetEvidenceLink} />
            )}
            <div className="text-xs text-gray-600 mb-2">
                {requirement.controls.length === 0
                    ? "No connected control evidences this requirement yet."
                    : `${requirement.controls.length} control${requirement.controls.length === 1 ? "" : "s"} · ${issues.length} open issue${issues.length === 1 ? "" : "s"}`}
            </div>
            <div className="space-y-2">
                {requirement.controls.map(rc => {
                    // The full Controls-tab row carries the evidence and the risk link.
                    const control = controlsById.get(rc.id) || rc;
                    const evidence = control.evidenceDetail?.summary || control.evidence;
                    return (
                        <div key={rc.id} className="border border-gray-200 rounded-lg bg-white" data-testid={`grc-requirement-control-${rc.id}`}>
                            <div className="p-2 flex flex-wrap items-center gap-2">
                                <span className="text-xs font-semibold text-gray-800 mr-auto">{control.title}</span>
                                <span className="text-xs text-gray-500">{control.source}</span>
                                <span className="text-xs text-gray-600">{CONTROL_STATUS_LABEL[control.status] || control.status}</span>
                                {onOpenControl && (
                                    <button type="button" onClick={() => onOpenControl(control)}
                                        className="text-xs px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">
                                        Open in Controls
                                    </button>
                                )}
                            </div>
                            <div className="px-2 pb-2 text-xs text-gray-600">{evidence || "No evidence recorded."}</div>
                            <GrcControlWorkflow risks={risksForControl(control)} ownerName={control.remediationOwner}
                                onChanged={onWorkflowChanged} disabledReason={workflowBusyReason}
                                pending={!!workflow?.isPending?.(control)} evidenceControlId={control.controlId}
                                className="border-t border-gray-100 p-2" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// The requirement list's columns: id, text, owner, controls, status. On a phone the text takes the
// whole first line and the rest wrap under it.
const ROW_GRID = "grid grid-cols-2 gap-x-3 gap-y-1 items-start md:grid-cols-[7rem_minmax(0,1fr)_9rem_6.5rem_8rem] md:gap-x-4";

const StatusChip = ({ status }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${STATUS_STYLE[status] || STATUS_STYLE.NotAssessed}`}>
        {STATUS_LABEL[status] || status}
    </span>
);

const RequirementList = ({ data, focusControl, onClearFocus, detailProps, currentUserId }) => {
    const [status, setStatus] = useState("all");
    const [owner, setOwner] = useState("all");
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);
    const counts = useMemo(() => statusCounts(data.requirements), [data.requirements]);
    const shown = useMemo(() => filterRequirements(data.requirements, { status, search })
        .filter(r => matchesOwnerFilter(r, owner, currentUserId))
        .filter(r => !focusControl || r.controls.some(c => c.id === focusControl.id)),
        [data.requirements, status, owner, search, focusControl, currentUserId]);
    const groups = useMemo(() => groupRequirements(shown), [shown]);

    return (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="grc-requirement-list">
            <div className="p-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
                <div className="mr-auto">
                    <div className="text-sm font-semibold text-gray-800">{data.framework}</div>
                    <div className="text-xs text-gray-500">
                        {data.hasCatalog
                            ? "Scored against the official requirement list"
                            : "Requirement text available; status from mapped controls only"}
                        {" · "}{data.requirements.length} requirements
                    </div>
                </div>
                <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Requirement status"
                    data-testid="grc-requirement-status-filter"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                    <option value="all">All statuses ({data.requirements.length})</option>
                    {STATUS_ORDER.filter(s => counts[s]).map(s => (
                        <option key={s} value={s}>{STATUS_LABEL[s]} ({counts[s]})</option>
                    ))}
                </select>
                <select value={owner} onChange={e => setOwner(e.target.value)} aria-label="Requirement owner filter"
                    data-testid="grc-requirement-owner-filter"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                    <option value="all">All owners</option>
                    <option value="mine">Assigned to me</option>
                    <option value="unassigned">Unassigned</option>
                </select>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search requirements"
                    aria-label="Search requirements"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-48" />
            </div>
            {focusControl && (
                <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100 text-xs text-indigo-800 flex items-center gap-2" data-testid="grc-requirement-focus">
                    <span className="mr-auto">Requirements evidenced by <span className="font-semibold">{focusControl.title}</span></span>
                    <button type="button" onClick={onClearFocus} className="font-semibold hover:underline">Show all requirements</button>
                </div>
            )}

            {groups.length > 0 && (
                <div className={`${ROW_GRID} hidden md:grid px-3 py-1.5 border-b border-gray-200 text-xs font-semibold text-gray-500`}
                    data-testid="grc-requirement-columns">
                    <span>Requirement</span><span>Text</span><span>Owner</span><span className="text-right">Controls</span><span className="justify-self-end">Status</span>
                </div>
            )}
            {groups.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-400">
                    {data.requirements.length === 0
                        ? "Axaris holds no requirement list for this framework."
                        : focusControl && status === "all" && owner === "all" && !search
                            ? "This control evidences no requirement on this list."
                            : "No requirement matches these filters."}
                </div>
            ) : groups.map(group => (
                <div key={group.name} className="border-b border-gray-100 last:border-b-0">
                    <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {group.name} <span className="font-normal normal-case">({group.items.length})</span>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {group.items.map(r => (
                            <li key={r.id} data-testid={`grc-requirement-${r.id}`} className={openId === r.id ? "bg-gray-50" : ""}>
                                {/* Fixed columns (id, text, owner, controls, status) so every row lines up under the header. */}
                                <button type="button" onClick={() => setOpenId(openId === r.id ? null : r.id)}
                                    aria-expanded={openId === r.id}
                                    className={`${ROW_GRID} w-full text-left px-3 py-2 hover:bg-gray-50`}>
                                    <span className="text-xs font-semibold text-gray-800 break-words">{r.displayId}</span>
                                    <span className="text-xs text-gray-600 min-w-0 col-span-2 md:col-span-1 order-first md:order-none">{r.title}</span>
                                    <span className={`text-xs truncate ${r.ownerUserId == null || r.ownerLeft ? "text-amber-700" : "text-gray-600"}`}
                                        data-testid={`grc-requirement-owner-cell-${r.id}`}>
                                        {ownerLabel(r)}
                                    </span>
                                    <span className="text-xs text-gray-500 md:text-right">
                                        {r.controls.length === 0 ? "No controls" : `${r.controls.length} control${r.controls.length === 1 ? "" : "s"}`}
                                    </span>
                                    <span className="md:justify-self-end"><StatusChip status={r.status} /></span>
                                </button>
                                {openId === r.id && <RequirementDetail requirement={r} {...detailProps} />}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            {data.retiredRequirements?.length > 0 && (
                <div className="p-3 border-t border-gray-200" data-testid="grc-retired-requirements">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Retired requirements ({data.retiredRequirements.length})
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                        These requirements have an owner but are no longer on the framework's list. They are kept for the record.
                    </div>
                    <ul className="space-y-1">
                        {data.retiredRequirements.map(r => (
                            <li key={r.id} className="text-xs text-gray-700 flex items-center gap-2">
                                <span className="font-semibold">{r.id}</span>
                                <span className="text-gray-500 flex-1 min-w-0">{r.title}</span>
                                <span>{r.ownerLeft ? "Owner left" : r.ownerName}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {data.outsideCatalog.length > 0 && (
                <div className="p-3 border-t border-gray-200" data-testid="grc-outside-catalog">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Mapped references outside this catalog ({data.outsideCatalog.length})
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                        Controls map to these references, but the requirement list above does not contain them.
                    </div>
                    <ul className="space-y-1">
                        {data.outsideCatalog.map(o => (
                            <li key={o.requirementId} className="text-xs text-gray-700 flex items-center gap-2">
                                <span className="font-semibold">{o.requirementId}</span>
                                <span className="text-gray-500">{o.controls.map(c => c.title).join(", ")}</span>
                                <StatusChip status={o.status} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// focus: { framework, control } from a control's "View requirements" (framework as the control's
// mapping names it; the server resolves it). onClearFocus drops it.
// canManageOwners: may set requirement owners (the server checks again); currentUserId drives Assigned to me.
const GrcFrameworksTab = ({
    estimates, controls = [], workflow = null, onWorkflowChanged, workflowBusyReason = null,
    onOpenControl, focus = null, onClearFocus, canManageOwners = false, currentUserId = null,
    evidenceRequestsEnabled = false, summaryLoading = false, summaryFailed = false,
}) => {
    const apiCall = useApi();
    const [selected, setSelected] = useState(null);
    const [state, setState] = useState({ loading: false, error: null, data: null });
    const [reloadTick, setReloadTick] = useState(0);
    const [members, setMembers] = useState([]);
    const requestSeq = useRef(0);
    const loadedFramework = useRef(null);

    // The owner picker's people: every current member, read once for a manager.
    useEffect(() => {
        if (!canManageOwners) return;
        apiCall("GET", "/security-insights/policy-docs/members")
            .then(list => setMembers(Array.isArray(list) ? list : []))
            .catch(() => setMembers([]));
    }, [apiCall, canManageOwners]);

    // The requests a manager can link: the Evidence Requests list, read once.
    const [evidenceRequests, setEvidenceRequests] = useState([]);
    useEffect(() => {
        if (!canManageOwners || !evidenceRequestsEnabled) return;
        apiCall("GET", "/security-insights/evidence/requests")
            .then(list => setEvidenceRequests(Array.isArray(list) ? list : []))
            .catch(() => setEvidenceRequests([]));
    }, [apiCall, canManageOwners, evidenceRequestsEnabled]);

    const list = useMemo(() => estimates || [], [estimates]);
    const firstFramework = list[0]?.framework;
    useEffect(() => {
        if (focus?.framework) setSelected(focus.framework);
    }, [focus]);
    useEffect(() => {
        if (!selected && !focus && firstFramework) setSelected(firstFramework);
    }, [firstFramework, selected, focus]);
    // A framework the view no longer shows (Frameworks shown, above the tabs) gives way to the first
    // one shown. The selection may be a control mapping's own name, so the server's answer counts too.
    const listNames = list.map(e => e.framework).join("|");
    const answeredFramework = state.data?.framework;
    useEffect(() => {
        const names = listNames ? listNames.split("|") : [];
        if (focus || !selected || names.length === 0) return;
        if (!names.includes(selected) && !names.includes(answeredFramework)) setSelected(names[0]);
    }, [listNames, selected, focus, answeredFramework]);
    const controlsById = useMemo(() => new Map((controls || []).map(c => [c.id, c])), [controls]);
    // The card stays highlighted on the canonical name the server answered with.
    const shownFramework = state.data?.framework || selected;
    const selectFramework = (framework) => {
        if (focus) onClearFocus?.();
        setSelected(framework);
    };

    useEffect(() => {
        if (!selected) return;
        // Only the latest selection may write state, so a slow earlier read cannot overwrite it.
        const seq = ++requestSeq.current;
        // A reload after an owner change keeps the list (and the open requirement) on screen.
        const silent = reloadTick > 0 && loadedFramework.current === selected;
        if (!silent) setState({ loading: true, error: null, data: null });
        apiCall("GET", `/security-insights/framework-requirements?framework=${encodeURIComponent(selected)}`)
            .then(data => {
                if (seq !== requestSeq.current) return;
                loadedFramework.current = selected;
                setState({ loading: false, error: null, data });
            })
            .catch(err => {
                if (seq !== requestSeq.current) return;
                if (silent) return; // keep what is shown; the owner call itself reported any error
                const notApplicable = err?.response?.status === 404;
                setState({
                    loading: false, data: null,
                    error: notApplicable ? "This framework does not apply to the organization." : "Could not load the requirements. Try again.",
                });
            });
    }, [apiCall, selected, reloadTick]);

    // Throws on failure so the picker can show the server's message.
    const setOwner = async (requirement, ownerUserId) => {
        await apiCall("PUT", "/security-insights/framework-requirements/owner", {
            framework: state.data.framework, requirementId: requirement.id, ownerUserId,
        });
        setReloadTick(t => t + 1);
    };

    const setEvidenceLink = async (requirement, evidenceRequestId, linked) => {
        await apiCall("PUT", "/security-insights/framework-requirements/evidence-links", {
            framework: state.data.framework, requirementId: requirement.id, evidenceRequestId, linked,
        });
        setReloadTick(t => t + 1);
    };

    // The frameworks come with the page's controls summary: say it is loading, or that it failed, rather
    // than that nothing is scored.
    if (list.length === 0 && !focus) {
        if (summaryLoading) return <div className="text-center text-sm text-gray-400 py-10 animate-pulse">Loading frameworks…</div>;
        if (summaryFailed)
            return <div className="text-center text-sm text-red-600 py-10" role="alert">Could not load the frameworks. Use Refresh Data or reload the page.</div>;
        return <div className="text-center text-sm text-gray-400 py-10">No framework is scored for this organization yet.</div>;
    }

    return (
        <div className="space-y-4" data-testid="grc-frameworks-tab">
            <FrameworkReadinessChart estimates={list} selected={shownFramework} onSelect={selectFramework}
                optionLabel={(e) => `${e.framework} — ${readinessText(e)}`} />
            {state.loading && <div className="text-center text-xs text-gray-400 py-6">Loading requirements…</div>}
            {state.error && <div className="text-center text-xs text-red-600 py-6" role="alert">{state.error}</div>}
            {state.data && (
                <RequirementList key={state.data.framework} data={state.data} currentUserId={currentUserId}
                    focusControl={focus?.control || null} onClearFocus={onClearFocus}
                    detailProps={{
                        controlsById, workflow, onWorkflowChanged, workflowBusyReason, onOpenControl,
                        members, canManageOwners, onSetOwner: setOwner,
                        evidenceRequestsEnabled, evidenceRequests, onSetEvidenceLink: setEvidenceLink,
                    }} />
            )}
        </div>
    );
};

export default GrcFrameworksTab;
